/**
 * WebOs Gatekeeper
 *
 * Contrôle d'accès préventif.
 * Valide les manifests et vérifie les permissions des applications.
 */

import { createLogger } from '../utils/debug.js';

const log = createLogger('Gate');

// Permissions standards (pas de sudo requis)
const STANDARD_PERMISSIONS = [
  'fs.read',
  'fs.write',
  'fs.delete',
  'net.fetch',
  'net.websocket',
  'process.spawn',
  'process.list',
  'env.read',
  'clipboard.read',
  'clipboard.write',
  'notification'
];

// Permissions privilégiées (requièrent sudo)
const PRIVILEGED_PERMISSIONS = [
  'fs.write.system',
  'process.kill',
  'env.write',
  'storage.quota'
];

// Toutes les permissions valides
const ALL_PERMISSIONS = [...STANDARD_PERMISSIONS, ...PRIVILEGED_PERMISSIONS];

// Regex pour valider le nom d'application
const NAME_REGEX = /^[a-z0-9]([a-z0-9-]*[a-z0-9])?$/;

// Regex pour valider la version semver (simplifiée)
const VERSION_REGEX = /^\d+\.\d+\.\d+(-[a-zA-Z0-9.]+)?$/;

/**
 * Résultat de validation
 * @typedef {Object} ValidationResult
 * @property {boolean} valid - Si le manifest est valide
 * @property {string[]} errors - Liste des erreurs
 * @property {string[]} warnings - Liste des avertissements
 * @property {string[]} privilegedPermissions - Permissions nécessitant sudo
 */

/**
 * Classe Gatekeeper
 */
export class Gatekeeper {
  constructor() {
    log.debug('Gatekeeper initialized');
  }

  /**
   * Valide un manifest d'application
   * @param {Object} manifest
   * @returns {ValidationResult}
   */
  validateManifest(manifest) {
    const errors = [];
    const warnings = [];
    const privilegedPermissions = [];

    log.debug('Validating manifest:', manifest?.name || 'unknown');

    // Vérifier que c'est un objet
    if (!manifest || typeof manifest !== 'object') {
      errors.push('Manifest must be a valid JSON object');
      return { valid: false, errors, warnings, privilegedPermissions };
    }

    // Champs obligatoires
    if (!manifest.name) {
      errors.push('Missing required field: name');
    } else if (!NAME_REGEX.test(manifest.name)) {
      errors.push(`Invalid name format: "${manifest.name}". Must match /^[a-z0-9]([a-z0-9-]*[a-z0-9])?$/`);
    } else if (manifest.name.length < 2 || manifest.name.length > 50) {
      errors.push('Name must be between 2 and 50 characters');
    }

    if (!manifest.version) {
      errors.push('Missing required field: version');
    } else if (!VERSION_REGEX.test(manifest.version)) {
      errors.push(`Invalid version format: "${manifest.version}". Must be semver (e.g., 1.0.0)`);
    }

    if (!manifest.type) {
      errors.push('Missing required field: type');
    } else if (!['tui', 'gui', 'background'].includes(manifest.type)) {
      errors.push(`Invalid type: "${manifest.type}". Must be "tui", "gui", or "background"`);
    }

    if (!manifest.entry) {
      errors.push('Missing required field: entry');
    }

    // Vérifier les permissions
    if (manifest.permissions) {
      if (!Array.isArray(manifest.permissions)) {
        errors.push('permissions must be an array');
      } else {
        for (const perm of manifest.permissions) {
          if (!ALL_PERMISSIONS.includes(perm)) {
            errors.push(`Unknown permission: "${perm}"`);
          } else if (PRIVILEGED_PERMISSIONS.includes(perm)) {
            privilegedPermissions.push(perm);
          }
        }
      }
    }

    // Vérifier privileged
    if (manifest.privileged === true) {
      privilegedPermissions.push('privileged');
      warnings.push('Application requests full privileged access');
    }

    // Vérifier les dépendances
    if (manifest.dependencies) {
      if (typeof manifest.dependencies !== 'object') {
        errors.push('dependencies must be an object');
      }
    }

    // Avertissements optionnels
    if (!manifest.description) {
      warnings.push('Missing optional field: description');
    }

    if (!manifest.author) {
      warnings.push('Missing optional field: author');
    }

    const valid = errors.length === 0;

    if (valid) {
      log.debug('Manifest valid:', manifest.name);
    } else {
      log.warn('Manifest invalid:', manifest.name, errors);
    }

    return { valid, errors, warnings, privilegedPermissions };
  }

  /**
   * Vérifie si une permission est accordée
   * @param {string[]} grantedPermissions - Permissions du manifest
   * @param {string} requiredPermission - Permission requise
   * @returns {boolean}
   */
  hasPermission(grantedPermissions, requiredPermission) {
    if (!grantedPermissions || !Array.isArray(grantedPermissions)) {
      return false;
    }

    // Permission exacte
    if (grantedPermissions.includes(requiredPermission)) {
      return true;
    }

    // Permission wildcard (ex: fs.* couvre fs.read, fs.write)
    const parts = requiredPermission.split('.');
    for (let i = parts.length - 1; i > 0; i--) {
      const wildcard = parts.slice(0, i).join('.') + '.*';
      if (grantedPermissions.includes(wildcard)) {
        return true;
      }
    }

    // privileged accorde tout
    if (grantedPermissions.includes('privileged')) {
      return true;
    }

    return false;
  }

  /**
   * Vérifie l'accès au système de fichiers
   * @param {string[]} permissions - Permissions de l'app
   * @param {string} path - Chemin demandé
   * @param {string} operation - 'read' | 'write' | 'delete'
   * @returns {{allowed: boolean, reason?: string}}
   */
  checkFileAccess(permissions, path, operation) {
    log.debug(`Checking file access: ${operation} ${path}`);

    // Lecture
    if (operation === 'read') {
      if (!this.hasPermission(permissions, 'fs.read')) {
        return { allowed: false, reason: 'Permission fs.read required' };
      }
      return { allowed: true };
    }

    // Écriture
    if (operation === 'write') {
      // /home et /tmp sont accessibles avec fs.write
      if (path.startsWith('/home/') || path.startsWith('/tmp/')) {
        if (!this.hasPermission(permissions, 'fs.write')) {
          return { allowed: false, reason: 'Permission fs.write required' };
        }
        return { allowed: true };
      }

      // Autres chemins nécessitent fs.write.system
      if (!this.hasPermission(permissions, 'fs.write.system')) {
        return {
          allowed: false,
          reason: `Permission fs.write.system required for writing to ${path}`
        };
      }
      return { allowed: true };
    }

    // Suppression
    if (operation === 'delete') {
      if (!this.hasPermission(permissions, 'fs.delete')) {
        return { allowed: false, reason: 'Permission fs.delete required' };
      }

      // Suppression système
      if (!path.startsWith('/home/') && !path.startsWith('/tmp/')) {
        if (!this.hasPermission(permissions, 'fs.write.system')) {
          return {
            allowed: false,
            reason: `Permission fs.write.system required for deleting ${path}`
          };
        }
      }
      return { allowed: true };
    }

    return { allowed: false, reason: 'Unknown operation' };
  }

  /**
   * Vérifie si des permissions privilégiées sont requises
   * @param {string[]} permissions
   * @returns {string[]} Liste des permissions privilégiées
   */
  getPrivilegedPermissions(permissions) {
    if (!permissions || !Array.isArray(permissions)) {
      return [];
    }

    return permissions.filter(p => PRIVILEGED_PERMISSIONS.includes(p));
  }

  /**
   * Vérifie si une application nécessite une élévation sudo
   * @param {Object} manifest
   * @returns {boolean}
   */
  requiresSudo(manifest) {
    if (manifest.privileged === true) {
      return true;
    }

    const privileged = this.getPrivilegedPermissions(manifest.permissions || []);
    return privileged.length > 0;
  }

  /**
   * Crée un contexte de sécurité pour un processus
   * @param {Object} manifest
   * @param {number} uid
   * @returns {Object}
   */
  createSecurityContext(manifest, uid = 1000) {
    return {
      uid,
      gid: uid,
      groups: [uid],
      permissions: manifest.permissions || [],
      elevated: manifest.privileged === true,
      sessionToken: crypto.randomUUID(),
      expiresAt: null
    };
  }

  /**
   * Valide une requête IPC selon les permissions
   * @param {string[]} permissions - Permissions du processus
   * @param {string} requestType - Type de requête IPC
   * @param {Object} payload - Contenu de la requête
   * @returns {{allowed: boolean, reason?: string}}
   */
  validateRequest(permissions, requestType, payload) {
    log.debug(`Validating request: ${requestType}`);

    // Requêtes FS
    if (requestType === 'REQ_FS_READ') {
      return this.checkFileAccess(permissions, payload.path, 'read');
    }

    if (requestType === 'REQ_FS_WRITE') {
      return this.checkFileAccess(permissions, payload.path, 'write');
    }

    if (requestType === 'REQ_FS_DELETE') {
      return this.checkFileAccess(permissions, payload.path, 'delete');
    }

    if (requestType === 'REQ_FS_MKDIR') {
      return this.checkFileAccess(permissions, payload.path, 'write');
    }

    // Autres requêtes FS (stat, readdir, exists) = read
    if (requestType.startsWith('REQ_FS_')) {
      return this.checkFileAccess(permissions, payload.path, 'read');
    }

    // Requêtes réseau
    if (requestType === 'REQ_NET_FETCH') {
      if (!this.hasPermission(permissions, 'net.fetch')) {
        return { allowed: false, reason: 'Permission net.fetch required' };
      }
      return { allowed: true };
    }

    // Requêtes processus
    if (requestType === 'REQ_SPAWN') {
      if (!this.hasPermission(permissions, 'process.spawn')) {
        return { allowed: false, reason: 'Permission process.spawn required' };
      }
      return { allowed: true };
    }

    if (requestType === 'REQ_KILL') {
      if (!this.hasPermission(permissions, 'process.kill')) {
        return { allowed: false, reason: 'Permission process.kill required' };
      }
      return { allowed: true };
    }

    if (requestType === 'REQ_PROCESS_LIST') {
      if (!this.hasPermission(permissions, 'process.list')) {
        return { allowed: false, reason: 'Permission process.list required' };
      }
      return { allowed: true };
    }

    // Par défaut, autoriser (pour les requêtes système internes)
    return { allowed: true };
  }
}

// Instance singleton
let instance = null;

/**
 * Obtient l'instance du Gatekeeper
 * @returns {Gatekeeper}
 */
export function getGatekeeper() {
  if (!instance) {
    instance = new Gatekeeper();
  }
  return instance;
}
